import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";

export interface ManifestNote {
  id?: string | number;
  manifestNumber: number;
  note: string;
  noteType: string;
  createdBy?: string;
  updatedBy?: string;
  dateCreated?: string;
  dateUpdated?: string;
}

export const manifestNoteTypeOptions = [
  { label: "Driver Operations", value: "Driver Operations" },
  { label: "General", value: "General" },
  { label: "Customer Service", value: "Customer Service" },
  { label: "Other", value: "Other" },
];

/** Notes are loaded from manifest details API. This hook provides only mutations (add/update/delete). */
export const useManifestNotes = (manifestNumber?: string | number | null) => {
  const queryClient = useQueryClient();
  const manifestDetailsKey = [QUERY_KEYS.MANIFEST_DETAILS, manifestNumber];

  const addNoteMutation = useMutation({
    mutationFn: async (payload: { manifestNumber: number; note: string; noteType: string }) => {
      await apiQueryAdapter.query(
        "/linehaul-manifests/notes",
        "POST",
        { body: payload }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manifestDetailsKey });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({
      noteId,
      payload,
    }: {
      noteId: string | number;
      payload: { manifestNumber: number; note: string; noteType: string };
    }) => {
      await apiQueryAdapter.query(
        `/linehaul-manifests/notes/${noteId}`,
        "PUT",
        { body: payload }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manifestDetailsKey });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string | number) => {
      await apiQueryAdapter.query(
        `/linehaul-manifests/notes/${noteId}`,
        "DELETE"
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manifestDetailsKey });
    },
  });

  return {
    addNote: addNoteMutation.mutateAsync,
    addNoteLoading: addNoteMutation.isLoading,
    updateNote: updateNoteMutation.mutateAsync,
    updateNoteLoading: updateNoteMutation.isLoading,
    deleteNote: deleteNoteMutation.mutateAsync,
    deleteNoteLoading: deleteNoteMutation.isLoading,
  };
};
